/**
 * NOCs — issue, list, print (browser → PDF), revoke.
 *
 * Filter pills cover all 8 NocPurpose values. Print modal renders the API's
 * `/v1/nocs/:id/print.html` inside an iframe — server-side inline styles mean
 * the printout works the same in browser preview and in Cmd+P.
 */
import { useMemo, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  FileSignature,
  Plus,
  MoreHorizontal,
  Printer,
  Link as LinkIcon,
  ShieldOff
} from 'lucide-react';
import { toast } from 'sonner';
import { get, post, getErrorMessage } from '@/lib/api';
import { useAuthStore } from '@/lib/auth';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TabsPills } from '@/components/ui/tabs-pills';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { CursorPaginator } from '@/components/ui/cursor-paginator';
import { useCursorAccumulator } from '@/hooks/useCursorAccumulator';
import { formatDateTime, formatDate } from '@/lib/format';

// ─── types ───

type NocPurpose =
  | 'move_out'
  | 'vehicle_registration'
  | 'passport'
  | 'electricity_connection'
  | 'gas_connection'
  | 'internet_connection'
  | 'general'
  | 'other';

interface UnitLite {
  id: string;
  number: string;
  block: { id: string; name: string } | null;
}

interface ResidentLite {
  id: string;
  full_name: string;
  phone: string;
  unit_id: string;
}

interface Noc {
  id: string;
  society_id: string;
  transition_id: string | null;
  unit_id: string;
  resident_id: string | null;
  resident_full_name: string;
  purpose: NocPurpose;
  noc_number: string;
  details: string | null;
  pending_dues_inr: number;
  issued_at: string;
  valid_until: string | null;
  revoked_at: string | null;
  revoke_reason: string | null;
  unit: UnitLite;
  transition: { id: string; kind: string; status: string } | null;
}

interface ListResponse {
  items: Noc[];
  next_cursor: string | null;
}

interface UnitListResponse {
  items: UnitLite[];
}

interface ResidentListResponse {
  items: ResidentLite[];
}

// ─── labels ───

const PURPOSE_LABEL: Record<NocPurpose, string> = {
  move_out: 'Move-out',
  vehicle_registration: 'Vehicle registration',
  passport: 'Passport',
  electricity_connection: 'Electricity',
  gas_connection: 'Gas',
  internet_connection: 'Internet',
  general: 'General',
  other: 'Other'
};

const PURPOSE_OPTIONS: NocPurpose[] = [
  'move_out',
  'vehicle_registration',
  'passport',
  'electricity_connection',
  'gas_connection',
  'internet_connection',
  'general',
  'other'
];

const PURPOSE_VARIANT: Record<NocPurpose, 'info' | 'warn' | 'ok' | 'muted' | 'alert'> = {
  move_out: 'alert',
  vehicle_registration: 'info',
  passport: 'info',
  electricity_connection: 'warn',
  gas_connection: 'warn',
  internet_connection: 'ok',
  general: 'muted',
  other: 'muted'
};

// ─── form schema ───

const IssueSchema = z.object({
  purpose: z.enum([
    'move_out',
    'vehicle_registration',
    'passport',
    'electricity_connection',
    'gas_connection',
    'internet_connection',
    'general',
    'other'
  ]),
  transition_id: z.string().uuid().optional().or(z.literal('')),
  unit_id: z.string().uuid({ message: 'Unit required' }),
  resident_id: z.string().uuid().optional().or(z.literal('')),
  resident_full_name: z.string().trim().min(2, 'Name required').max(120),
  details: z.string().trim().max(1000).optional().or(z.literal('')),
  valid_until: z.string().optional().or(z.literal(''))
});
type IssueForm = z.infer<typeof IssueSchema>;

const RevokeSchema = z.object({ reason: z.string().trim().min(2, 'Reason required').max(300) });
type RevokeForm = z.infer<typeof RevokeSchema>;

// ─── helpers ───

function unitLabel(u: UnitLite | null | undefined): string {
  if (!u) return '—';
  return u.block ? `${u.block.name} / ${u.number}` : u.number;
}

function toIsoDateTime(yyyyMmDd: string): string | undefined {
  if (!yyyyMmDd) return undefined;
  return new Date(`${yyyyMmDd}T00:00:00.000Z`).toISOString();
}

function readQueryParam(name: string): string | null {
  if (typeof window === 'undefined') return null;
  const u = new URL(window.location.href);
  return u.searchParams.get(name);
}

// ─── component ───

export function NocsPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [purposeFilter, setPurposeFilter] = useState<'all' | NocPurpose>('all');
  const [issueOpen, setIssueOpen] = useState(false);
  const [printTarget, setPrintTarget] = useState<Noc | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<Noc | null>(null);
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);

  // Open issue dialog automatically when reaching this page with prefilled query
  useEffect(() => {
    const tid = readQueryParam('transition_id');
    const uid = readQueryParam('unit_id');
    if (tid || uid) setIssueOpen(true);
  }, []);

  // Queries — cursor paginated.
  const nocsFiltersHash = useMemo(() => JSON.stringify([purposeFilter]), [purposeFilter]);
  const [nocsCursor, setNocsCursor] = useState<string | null>(null);
  useEffect(() => {
    setNocsCursor(null);
  }, [nocsFiltersHash]);
  const nocsQuery = useQuery({
    queryKey: ['nocs', purposeFilter, nocsCursor],
    queryFn: () =>
      get<ListResponse>('/nocs', {
        params: {
          limit: 50,
          ...(purposeFilter !== 'all' ? { purpose: purposeFilter } : {}),
          ...(nocsCursor ? { cursor: nocsCursor } : {})
        }
      })
  });
  const nocsPaginator = useCursorAccumulator<Noc>(nocsQuery.data, nocsCursor, nocsFiltersHash);
  const nocs = nocsPaginator.items;

  const unitsQuery = useQuery({
    queryKey: ['noc-units'],
    queryFn: () => get<UnitListResponse>('/society/units', { params: { limit: 500 } }),
    staleTime: 60_000
  });
  const units = unitsQuery.data?.items ?? [];

  const residentsQuery = useQuery({
    queryKey: ['noc-residents'],
    queryFn: () => get<ResidentListResponse>('/residents', { params: { limit: 500 } }),
    staleTime: 60_000
  });
  const residents = residentsQuery.data?.items ?? [];

  // Issue form
  const issueForm = useForm<IssueForm>({
    resolver: zodResolver(IssueSchema),
    mode: 'onChange',
    defaultValues: {
      purpose: 'general',
      transition_id: readQueryParam('transition_id') ?? '',
      unit_id: readQueryParam('unit_id') ?? '',
      resident_id: '',
      resident_full_name: '',
      details: '',
      valid_until: ''
    }
  });

  const watchUnitId = issueForm.watch('unit_id');
  const watchResidentId = issueForm.watch('resident_id');

  const eligibleResidents = useMemo(
    () => residents.filter((r) => r.unit_id === watchUnitId),
    [residents, watchUnitId]
  );

  // Auto-fill resident_full_name when resident picked
  useEffect(() => {
    if (!watchResidentId) return;
    const r = residents.find((x) => x.id === watchResidentId);
    if (r) issueForm.setValue('resident_full_name', r.full_name, { shouldValidate: true });
  }, [watchResidentId, residents, issueForm]);

  const issue = useMutation({
    mutationFn: (v: IssueForm) =>
      post<Noc>('/nocs', {
        purpose: v.purpose,
        unit_id: v.unit_id,
        resident_full_name: v.resident_full_name,
        ...(v.transition_id ? { transition_id: v.transition_id } : {}),
        ...(v.resident_id ? { resident_id: v.resident_id } : {}),
        ...(v.details ? { details: v.details } : {}),
        ...(v.valid_until ? { valid_until: toIsoDateTime(v.valid_until) } : {})
      }),
    onSuccess: (created) => {
      toast.success(`NOC ${created.noc_number} issued`);
      setIssueOpen(false);
      issueForm.reset({
        purpose: 'general',
        transition_id: '',
        unit_id: '',
        resident_id: '',
        resident_full_name: '',
        details: '',
        valid_until: ''
      });
      // Clear ?transition_id from URL once consumed
      if (typeof window !== 'undefined') {
        const u = new URL(window.location.href);
        u.searchParams.delete('transition_id');
        u.searchParams.delete('unit_id');
        window.history.replaceState({}, '', u.toString());
      }
      void queryClient.invalidateQueries({ queryKey: ['nocs'] });
      void queryClient.invalidateQueries({ queryKey: ['tenancy-transitions'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  // Revoke
  const revokeForm = useForm<RevokeForm>({ resolver: zodResolver(RevokeSchema), defaultValues: { reason: '' } });
  const revoke = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      post<Noc>(`/nocs/${id}/revoke`, { reason }),
    onSuccess: () => {
      toast.success('NOC revoked');
      setRevokeTarget(null);
      revokeForm.reset({ reason: '' });
      void queryClient.invalidateQueries({ queryKey: ['nocs'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  // Print
  const apiBase = (import.meta.env.VITE_API_URL ?? 'http://localhost:4100') + '/v1';
  const accessToken = useAuthStore((s) => s.accessToken);
  const [printHtml, setPrintHtml] = useState<string | null>(null);

  useEffect(() => {
    if (!printTarget) {
      setPrintHtml(null);
      return;
    }
    void (async () => {
      try {
        const res = await fetch(`${apiBase}/nocs/${printTarget.id}/print.html`, {
          headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {}
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const html = await res.text();
        setPrintHtml(html);
      } catch (e) {
        toast.error(getErrorMessage(e));
        setPrintTarget(null);
      }
    })();
  }, [printTarget, apiBase, accessToken]);

  // Copy link to print HTML
  const copyPrintLink = async (n: Noc): Promise<void> => {
    const url = `${apiBase}/nocs/${n.id}/print.html`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Print link copied');
    } catch {
      toast.error('Copy failed');
    }
  };

  // ─── handlers ───

  const onSubmitIssue = (v: IssueForm): void => {
    issue.mutate(v);
  };
  const onSubmitRevoke = (v: RevokeForm): void => {
    if (!revokeTarget) return;
    revoke.mutate({ id: revokeTarget.id, reason: v.reason });
  };

  const openIssue = (): void => {
    issueForm.reset({
      purpose: 'general',
      transition_id: '',
      unit_id: '',
      resident_id: '',
      resident_full_name: '',
      details: '',
      valid_until: ''
    });
    setIssueOpen(true);
  };

  const printNow = (): void => {
    const iframe = document.getElementById('noc-print-iframe') as HTMLIFrameElement | null;
    if (!iframe || !iframe.contentWindow) {
      toast.error('Print preview not ready');
      return;
    }
    iframe.contentWindow.focus();
    iframe.contentWindow.print();
  };

  return (
    <>
      <PageHeader
        title="NOCs"
        description="Issue, print and track No Objection Certificates for residents — move-out, utilities, vehicles and more."
        actions={
          <Button size="sm" onClick={openIssue}>
            <Plus className="h-4 w-4" /> Issue NOC
          </Button>
        }
      />
      <div className="mx-auto w-full max-w-[1400px] space-y-vt-4 px-vt-6 pb-vt-8 pt-vt-4">
        {/* Purpose pills */}
        <TabsPills
          size="sm"
          value={purposeFilter}
          onChange={(v) => setPurposeFilter(v as 'all' | NocPurpose)}
          options={[
            { value: 'all', label: 'All' },
            ...PURPOSE_OPTIONS.map((p) => ({ value: p, label: PURPOSE_LABEL[p] }))
          ]}
          aria-label="NOC purpose filter"
        />

        <Card>
          <CardContent className="p-0">
            {nocsQuery.isLoading ? (
              <div className="space-y-vt-2 p-vt-4">
                {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
              </div>
            ) : nocs.length === 0 ? (
              <EmptyState
                icon={<FileSignature className="h-6 w-6" />}
                title="No NOCs yet"
                description="Issue a No Objection Certificate when a resident requests one."
              />
            ) : (
              <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>NOC No.</TableHead>
                    <TableHead>Resident</TableHead>
                    <TableHead>Unit</TableHead>
                    <TableHead>Purpose</TableHead>
                    <TableHead>Issued</TableHead>
                    <TableHead>Valid until</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-[44px]"> </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {nocs.map((n) => (
                    <TableRow key={n.id}>
                      <TableCell className="font-mono text-vt-13">{n.noc_number}</TableCell>
                      <TableCell className="font-medium text-vt-text">{n.resident_full_name}</TableCell>
                      <TableCell>{unitLabel(n.unit)}</TableCell>
                      <TableCell>
                        <Badge variant={PURPOSE_VARIANT[n.purpose]}>{PURPOSE_LABEL[n.purpose]}</Badge>
                      </TableCell>
                      <TableCell>{formatDateTime(n.issued_at)}</TableCell>
                      <TableCell>{n.valid_until ? formatDate(n.valid_until) : '—'}</TableCell>
                      <TableCell>
                        {n.revoked_at ? <Badge variant="alert">Revoked</Badge> : <Badge variant="ok">Active</Badge>}
                      </TableCell>
                      <TableCell>
                        <div className="relative">
                          <button
                            type="button"
                            aria-label="Actions"
                            className="inline-flex h-7 w-7 items-center justify-center rounded-vt-sm text-vt-text-muted hover:bg-vt-surface-2 hover:text-vt-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vt-primary-400"
                            onClick={() => setMenuOpenId((id) => (id === n.id ? null : n.id))}
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </button>
                          {menuOpenId === n.id ? (
                            <div
                              className="absolute right-0 top-8 z-20 w-48 overflow-hidden rounded-vt-md border border-vt-border bg-vt-surface shadow-vt-2"
                              onMouseLeave={() => setMenuOpenId(null)}
                            >
                              <MenuItem
                                icon={<Printer className="h-4 w-4" />}
                                onClick={() => {
                                  setMenuOpenId(null);
                                  setPrintTarget(n);
                                }}
                              >
                                View / print
                              </MenuItem>
                              <MenuItem
                                icon={<LinkIcon className="h-4 w-4" />}
                                onClick={() => {
                                  setMenuOpenId(null);
                                  void copyPrintLink(n);
                                }}
                              >
                                Copy print link
                              </MenuItem>
                              {!n.revoked_at ? (
                                <MenuItem
                                  danger
                                  icon={<ShieldOff className="h-4 w-4" />}
                                  onClick={() => {
                                    setMenuOpenId(null);
                                    setRevokeTarget(n);
                                    revokeForm.reset({ reason: '' });
                                  }}
                                >
                                  Revoke
                                </MenuItem>
                              ) : null}
                            </div>
                          ) : null}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <CursorPaginator
                hasMore={Boolean(nocsPaginator.nextCursor)}
                loading={nocsQuery.isFetching}
                itemsShown={nocs.length}
                pageSize={50}
                onLoadMore={() => {
                  if (nocsPaginator.nextCursor) setNocsCursor(nocsPaginator.nextCursor);
                }}
              />
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Issue dialog */}
      <Dialog open={issueOpen} onOpenChange={(o) => setIssueOpen(o)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Issue NOC</DialogTitle>
            <DialogDescription>
              NOC number is auto-generated as NOC/FY/NNNN. Pending maintenance dues are auto-snapshotted.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={issueForm.handleSubmit(onSubmitIssue)} className="flex flex-col gap-vt-4">
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="purpose">Purpose</Label>
              <Select id="purpose" {...issueForm.register('purpose')}>
                {PURPOSE_OPTIONS.map((p) => (
                  <option key={p} value={p}>{PURPOSE_LABEL[p]}</option>
                ))}
              </Select>
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="i_unit_id">Unit</Label>
              <Select id="i_unit_id" {...issueForm.register('unit_id')}>
                <option value="">Select unit…</option>
                {units.map((u) => (
                  <option key={u.id} value={u.id}>
                    {unitLabel(u)}
                  </option>
                ))}
              </Select>
              {issueForm.formState.errors.unit_id ? (
                <p className="text-vt-12 text-vt-alert">{issueForm.formState.errors.unit_id.message}</p>
              ) : null}
            </div>
            <div className="grid grid-cols-2 gap-vt-3">
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="i_resident_id">Resident (optional)</Label>
                <Select id="i_resident_id" {...issueForm.register('resident_id')}>
                  <option value="">{watchUnitId ? 'Free-text below' : 'Pick a unit first'}</option>
                  {eligibleResidents.map((r) => (
                    <option key={r.id} value={r.id}>{r.full_name}</option>
                  ))}
                </Select>
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="resident_full_name">Resident name (on NOC)</Label>
                <Input id="resident_full_name" {...issueForm.register('resident_full_name')} />
                {issueForm.formState.errors.resident_full_name ? (
                  <p className="text-vt-12 text-vt-alert">{issueForm.formState.errors.resident_full_name.message}</p>
                ) : null}
              </div>
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="details">Details (printed on NOC)</Label>
              <Textarea id="details" rows={3} {...issueForm.register('details')} />
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="valid_until">Valid until (optional)</Label>
              <Input id="valid_until" type="date" {...issueForm.register('valid_until')} />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setIssueOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={issue.isPending}>
                {issue.isPending ? 'Issuing…' : 'Issue NOC'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Print modal */}
      <Dialog open={printTarget !== null} onOpenChange={(o) => (o ? null : setPrintTarget(null))}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>{printTarget ? printTarget.noc_number : 'NOC'}</DialogTitle>
            <DialogDescription>Preview of the printable certificate.</DialogDescription>
          </DialogHeader>
          {printHtml ? (
            <iframe
              id="noc-print-iframe"
              title="NOC preview"
              srcDoc={printHtml}
              className="h-[70vh] w-full rounded-vt-sm border border-vt-border bg-white"
            />
          ) : (
            <div className="flex h-[60vh] items-center justify-center">
              <Skeleton className="h-full w-full" />
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setPrintTarget(null)}>Close</Button>
            <Button type="button" onClick={printNow}>
              <Printer className="h-4 w-4" /> Print
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revoke dialog */}
      <Dialog open={revokeTarget !== null} onOpenChange={(o) => (o ? null : setRevokeTarget(null))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revoke NOC</DialogTitle>
            <DialogDescription>
              {revokeTarget ? (
                <>
                  Revoke <strong>{revokeTarget.noc_number}</strong> issued to <strong>{revokeTarget.resident_full_name}</strong>?
                  This is final but the document remains in the audit trail.
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={revokeForm.handleSubmit(onSubmitRevoke)} className="flex flex-col gap-vt-3">
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="r_reason">Reason</Label>
              <Textarea id="r_reason" rows={3} {...revokeForm.register('reason')} />
              {revokeForm.formState.errors.reason ? (
                <p className="text-vt-12 text-vt-alert">{revokeForm.formState.errors.reason.message}</p>
              ) : null}
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setRevokeTarget(null)}>Keep active</Button>
              <Button type="submit" variant="danger" disabled={revoke.isPending}>
                {revoke.isPending ? 'Revoking…' : 'Revoke NOC'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─── small components ───

function MenuItem({
  onClick,
  danger,
  icon,
  children
}: {
  onClick: () => void;
  danger?: boolean;
  icon?: React.ReactNode;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'flex w-full items-center gap-vt-2 px-vt-3 py-vt-2 text-left text-vt-13 hover:bg-vt-surface-2 ' +
        (danger ? 'text-vt-alert' : 'text-vt-text')
      }
    >
      {icon}
      {children}
    </button>
  );
}
